import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Btn, Chip } from '@/presentation/component/ui';
import { formatDate } from '@/presentation/util/format';
import { roleChipKind } from '@/presentation/util/roleChipKind';
import type { AdminUser } from '@/domain/model/AdminUser';

interface UserListProps {
  users: AdminUser[];
  isSuperAdmin: boolean;
  onViewProfile: (id: string) => void;
  onDelete: (id: string) => void;
}

const handleKeyDown = (callback: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    callback();
  }
};

interface DeleteButtonProps {
  label: string;
  onClick: (e: React.MouseEvent) => void;
}

const DeleteButton = ({ label, onClick }: DeleteButtonProps) => (
  <Btn
    size="sm"
    kind="ghost"
    aria-label={label}
    onClick={(e) => {
      e.stopPropagation();
      onClick(e);
    }}
  >
    <Trash2 className="h-4 w-4 text-[var(--color-bad)]" />
  </Btn>
);

interface UserRowProps {
  user: AdminUser;
  isSuperAdmin: boolean;
  deleteLabel: string;
  onViewProfile: (id: string) => void;
  onDelete: (id: string) => void;
}

const UserTableDesktop = ({ users, isSuperAdmin, onViewProfile, onDelete }: UserListProps) => {
  const { t } = useTranslation();

  return (
    <div className="mt-4 hidden overflow-x-auto md:block">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-[var(--color-line)] text-[var(--color-ink-faint)]">
            <th className="pb-3 pr-4 font-medium">{t('admin.userName')}</th>
            <th className="pb-3 pr-4 font-medium">{t('admin.userEmail')}</th>
            <th className="pb-3 pr-4 font-medium">{t('admin.userRole')}</th>
            <th className="pb-3 pr-4 font-medium">{t('admin.createdAt')}</th>
            {isSuperAdmin && <th className="pb-3 font-medium">{t('admin.actions')}</th>}
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <UserTableRow
              key={user.id}
              user={user}
              isSuperAdmin={isSuperAdmin}
              deleteLabel={t('admin.deleteUser')}
              onViewProfile={onViewProfile}
              onDelete={onDelete}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const UserTableRow = ({
  user,
  isSuperAdmin,
  deleteLabel,
  onViewProfile,
  onDelete,
}: UserRowProps) => {
  const { t } = useTranslation();

  return (
    <tr
      role="button"
      tabIndex={0}
      className="cursor-pointer border-b border-[var(--color-line)] text-[var(--color-ink)] hover:bg-[var(--color-surface-2)]"
      onClick={() => onViewProfile(user.id)}
      onKeyDown={handleKeyDown(() => onViewProfile(user.id))}
    >
      <td className="py-3 pr-4 font-medium">{user.name}</td>
      <td className="py-3 pr-4 text-[var(--color-ink-soft)]">{user.email}</td>
      <td className="py-3 pr-4">
        <Chip kind={roleChipKind(user.role)}>{t(`admin.role${user.role}`)}</Chip>
      </td>
      <td className="py-3 pr-4 text-[var(--color-ink-soft)]">{formatDate(user.createdAt)}</td>
      {isSuperAdmin && (
        <td className="py-3">
          <DeleteButton label={deleteLabel} onClick={() => onDelete(user.id)} />
        </td>
      )}
    </tr>
  );
};

const UserCardsMobile = ({ users, isSuperAdmin, onViewProfile, onDelete }: UserListProps) => {
  const { t } = useTranslation();

  return (
    <div className="mt-4 space-y-3 md:hidden">
      {users.map((user) => (
        <div
          key={user.id}
          role="button"
          tabIndex={0}
          className="cursor-pointer rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] p-4 hover:bg-[var(--color-surface-2)]"
          onClick={() => onViewProfile(user.id)}
          onKeyDown={handleKeyDown(() => onViewProfile(user.id))}
        >
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-[var(--color-ink)]">{user.name}</p>
              <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">{user.email}</p>
            </div>
            {isSuperAdmin && (
              <DeleteButton label={t('admin.deleteUser')} onClick={() => onDelete(user.id)} />
            )}
          </div>
          <div className="mt-2 flex items-center gap-3">
            <Chip kind={roleChipKind(user.role)}>{t(`admin.role${user.role}`)}</Chip>
            <span className="text-xs text-[var(--color-ink-faint)]">
              {formatDate(user.createdAt)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export const UserList = (props: UserListProps) => (
  <>
    <UserTableDesktop {...props} />
    <UserCardsMobile {...props} />
  </>
);
